import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Play, Pause, MoreVertical, Search, Copy, Share, Eye } from 'lucide-react';
import { botService } from '../../services/botService';
import { useNavigate } from 'react-router-dom';

const AdminBotManagement = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [duplicateBotName, setDuplicateBotName] = useState('');
  const [selectedBotForDuplicate, setSelectedBotForDuplicate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [unpublishBotId, setUnpublishBotId] = useState(null);
  const [activeSessionCount, setActiveSessionCount] = useState(0);

  useEffect(() => {
    loadAdminBots();
  }, [currentPage, sortBy, sortDir]);

  const loadAdminBots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await botService.getAdminBots(currentPage, 12, sortBy, sortDir);
      setBots(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Error loading admin bots:', error);
      setError('Failed to load bots. Please try again.');
      setBots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBot = async () => {
    if (!newBotName.trim()) {
      console.log('Bot name is empty, not creating bot');
      return;
    }

    console.log('Creating admin bot with name:', newBotName);
    
    try {
      setCreating(true);
      setError(null);
      
      const botData = {
        name: newBotName,
        description: '',
        status: '10001'
      };
      
      console.log('Calling botService.createAdminBot with data:', botData);
      const newBot = await botService.createAdminBot(botData);
      console.log('Admin bot created successfully:', newBot);
      
      // Navigate to bot builder for the new bot
      navigate(`/bots/builder/${newBot.botId}`);
      setShowCreateModal(false);
      setNewBotName('');
      loadAdminBots();
    } catch (error) {
      console.error('Error creating admin bot:', error);
      setError(`Failed to create bot: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleEditBot = (bot) => {
    // Navigate to bot builder for editing
    navigate(`/bots/builder/${bot.botId}`);
  };

  const handleDeleteBot = async (botId) => {
    if (window.confirm('Are you sure you want to delete this bot?')) {
      try {
        await botService.deleteAdminBot(botId);
        loadAdminBots();
      } catch (error) {
        console.error('Error deleting bot:', error);
        setError('Failed to delete bot. Please try again.');
      }
    }
  };

  const handlePublishBot = async (botId) => {
    try {
      await botService.updateAdminBot(botId, { status: '10002' });
      loadAdminBots();
    } catch (error) {
      console.error('Error publishing bot:', error);
      setError('Failed to publish bot. Please try again.');
    }
  };

  const handleUnpublishBot = async (botId) => {
    try {
      // Check for active sessions before unpublishing
      const sessionCount = await botService.getActiveSessionCount(botId);
      
      if (sessionCount > 0) {
        // Show confirmation dialog
        setUnpublishBotId(botId);
        setActiveSessionCount(sessionCount);
        setShowUnpublishConfirm(true);
        return;
      }
      
      // No active sessions, proceed with unpublish
      await performUnpublish(botId);
    } catch (error) {
      console.error('Error checking active sessions or unpublishing bot:', error);
      setError('Failed to unpublish bot. Please try again.');
    }
  };

  const performUnpublish = async (botId) => {
    try {
      await botService.updateAdminBot(botId, { status: '10001' });
      loadAdminBots();
    } catch (error) {
      console.error('Error unpublishing bot:', error);
      setError('Failed to unpublish bot. Please try again.');
    }
  };

  const handleConfirmUnpublish = async () => {
    if (unpublishBotId) {
      setShowUnpublishConfirm(false);
      await performUnpublish(unpublishBotId);
      setUnpublishBotId(null);
      setActiveSessionCount(0);
    }
  };

  const handleCancelUnpublish = () => {
    setShowUnpublishConfirm(false);
    setUnpublishBotId(null);
    setActiveSessionCount(0);
  };

  const handleDuplicateBot = (bot) => {
    setSelectedBotForDuplicate(bot);
    setDuplicateBotName(`${bot.botName} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateBotName.trim() || !selectedBotForDuplicate) {
      return;
    }

    try {
      setDuplicating(true);
      setError(null);
      // For admin, we'll create a new bot with the same configuration
      const newBot = await botService.createAdminBot({
        name: duplicateBotName.trim(),
        description: selectedBotForDuplicate.description,
        status: '10001'
      });
      setShowDuplicateModal(false);
      setDuplicateBotName('');
      setSelectedBotForDuplicate(null);
      loadAdminBots();
    } catch (error) {
      console.error('Error duplicating bot:', error);
      setError('Failed to duplicate bot. Please try again.');
    } finally {
      setDuplicating(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setCurrentPage(0);
  };

  const filteredBots = bots.filter(bot =>
    bot.botName && bot.botName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const normalizedStatus = status === '10001' ? 'DRAFT' : 
                            status === '10002' ? 'PUBLISHED' : 
                            status === '10003' ? 'ARCHIVED' : status;
    
    switch (normalizedStatus) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Bot Management</h1>
          <p className="text-gray-600">Manage public bots for all customers</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {totalElements} public bots
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create Bot</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search public bots..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Sort Options */}
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-600 font-medium">Sort by:</span>
        <div className="flex flex-wrap gap-2">
        {[
          { field: 'createdAt', label: 'Date Created' },
          { field: 'botName', label: 'Name' },
          { field: 'updatedAt', label: 'Last Updated' },
          { field: 'status', label: 'Status' }
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              sortBy === field
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label} {sortBy === field && (sortDir === 'asc' ? '↑' : '↓')}
          </button>
        ))}
        </div>
      </div>

      {/* Bots Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {filteredBots.map((bot) => (
            <div key={bot.botId} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-200">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{bot.botName}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{bot.description || 'No description'}</p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bot.botStatus)}`}>
                    {bot.botStatus === '10001' ? 'DRAFT' : 
                     bot.botStatus === '10002' ? 'PUBLISHED' : 
                     bot.botStatus === '10003' ? 'ARCHIVED' : bot.botStatus}
                  </span>
                  <button className="p-1 hover:bg-gray-100 rounded-md">
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-500 mb-4">
                <p>Created: {new Date(bot.createdAt).toLocaleDateString()}</p>
                <p>Updated: {new Date(bot.updatedAt).toLocaleDateString()}</p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditBot(bot)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center space-x-1 transition-colors"
                  title="View/Edit bot"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                
                <button
                  onClick={() => handlePublishBot(bot.botId)}
                  disabled={bot.botStatus === '10002'}
                  className={`px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors ${
                    bot.botStatus === '10002' 
                      ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                  title={bot.botStatus === '10002' ? 'Already Published' : 'Publish'}
                >
                  <Play className="w-4 h-4" />
                </button>

                <button
                  onClick={() => handleDuplicateBot(bot)}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                  title="Duplicate"
                >
                  <Copy className="w-4 h-4" />
                </button>

                <button
                  onClick={() => handleDeleteBot(bot.botId)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredBots.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="text-gray-400 mb-4">
            <Plus className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bots found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'No bots match your search.' : 'Get started by creating your first public bot.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create Bot</span>
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-12 flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 1, currentPage - 2 + i));
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 border rounded-md text-sm ${
                    currentPage === pageNum
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Bot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Public Bot</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Name
              </label>
              <input
                type="text"
                value={newBotName}
                onChange={(e) => setNewBotName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter bot name"
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreateBot}
                disabled={!newBotName.trim() || creating}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewBotName('');
                  setError(null);
                }}
                disabled={creating}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-4 py-2 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Bot Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Duplicate Bot</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Name
              </label>
              <input
                type="text"
                value={duplicateBotName}
                onChange={(e) => setDuplicateBotName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter bot name"
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleConfirmDuplicate}
                disabled={!duplicateBotName.trim() || duplicating}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
              >
                {duplicating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Duplicating...
                  </>
                ) : (
                  'Duplicate'
                )}
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateBotName('');
                  setSelectedBotForDuplicate(null);
                  setError(null);
                }}
                disabled={duplicating}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-4 py-2 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation Modal */}
      {showUnpublishConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Unpublish Bot
            </h3>
            <p className="text-gray-600 mb-6">
              This bot has {activeSessionCount} active user interaction(s). Unpublishing will end all active sessions. Do you want to continue?
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleCancelUnpublish}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUnpublish}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBotManagement;
